import React, { useState } from "react";
import commonStyles from "./DealerOnboarding.module.css";
import apiService from "../../services/apiService";

const styles = {
  container: {
    maxWidth: "800px",
    margin: "0 auto",
    padding: "2rem",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  formTitle: {
    fontSize: "2rem",
    fontWeight: "600",
    marginBottom: "2rem",
    color: "#1a202c",
  },
  section: {
    marginBottom: "2.5rem",
    padding: "1.5rem",
    backgroundColor: "#f7fafc",
    borderRadius: "8px",
    border: "1px solid #e2e8f0",
  },
  sectionTitle: {
    fontSize: "1.25rem",
    fontWeight: "600",
    marginBottom: "1.5rem",
    color: "#2d3748",
    borderBottom: "2px solid #f26422",
    paddingBottom: "0.5rem",
  },
  formGroup: {
    marginBottom: "1.25rem",
  },
  label: {
    display: "block",
    marginBottom: "0.5rem",
    fontWeight: "500",
    color: "#4a5568",
    fontSize: "0.875rem",
  },
  required: {
    color: "#e53e3e",
    marginLeft: "0.25rem",
  },
  input: {
    width: "100%",
    padding: "0.75rem",
    fontSize: "1rem",
    border: "1px solid #cbd5e0",
    borderRadius: "6px",
    transition: "border-color 0.2s, box-shadow 0.2s",
    boxSizing: "border-box",
  },
  inputFocus: {
    outline: "none",
    borderColor: "#f26422",
    boxShadow: "0 0 0 3px rgba(66, 153, 225, 0.1)",
  },
  select: {
    width: "100%",
    padding: "0.75rem",
    fontSize: "1rem",
    border: "1px solid #cbd5e0",
    borderRadius: "6px",
    backgroundColor: "white",
    cursor: "pointer",
    transition: "border-color 0.2s, box-shadow 0.2s",
    boxSizing: "border-box",
  },
  row: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "1rem",
  },
  submitButton: {
    width: "100%",
    padding: "1rem",
    fontSize: "1rem",
    fontWeight: "600",
    color: "white",
    backgroundColor: "#f26422",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    transition: "background-color 0.2s",
    marginTop: "1rem",
  },
  submitButtonHover: {
    backgroundColor: "#3182ce",
  },
};

const businessTypeFields = {
  proprietorship: [
    {
      name: "business_name",
      label: "Business Name",
      type: "text",
      required: true,
    },
    {
      name: "aadhaar_number",
      label: "Aadhaar Number",
      type: "text",
      required: true,
    },
    { name: "pan_number", label: "PAN Number", type: "text", required: true },
  ],
  partnership: [
    {
      name: "business_name",
      label: "Business Name",
      type: "text",
      required: true,
    },
  ],
  limited_liability_partnership: [
    {
      name: "business_name",
      label: "Business Name",
      type: "text",
      required: true,
    },
    {
      name: "company_pan_number",
      label: "Company PAN Number",
      type: "text",
      required: true,
    },
  ],
  one_person_company: [
    {
      name: "business_name",
      label: "Business Name",
      type: "text",
      required: true,
    },
    {
      name: "company_pan_number",
      label: "Company PAN Number",
      type: "text",
      required: true,
    },
    { name: "gst_number", label: "GSTIN Number", type: "text", required: true },
  ],
  private_limited_company: [
    {
      name: "business_name",
      label: "Business Name",
      type: "text",
      required: true,
    },
    {
      name: "company_pan_number",
      label: "Company PAN Number",
      type: "text",
      required: true,
    },
    { name: "gst_number", label: "GSTIN Number", type: "text", required: true },
  ],
  individual: [
    {
      name: "business_name",
      label: "Business Name",
      type: "text",
      required: true,
    },
    { name: "name", label: "Name", type: "text", required: true },
    {
      name: "aadhaar_number",
      label: "Aadhaar Number",
      type: "text",
      required: true,
    },
    { name: "pan_number", label: "PAN Number", type: "text", required: true },
  ],
};

const businessTypes = [
  { label: "Individual", value: "individual" },
  { label: "Proprietorship", value: "proprietorship" },
  { label: "Partnership", value: "partnership" },
  {
    label: "Limited Liability Partnership (LLP)",
    value: "limited_liability_partnership",
  },
  { label: "One Person Company", value: "one_person_company" },
  { label: "Private Limited Company", value: "private_limited_company" },
];

export default function DealerRegistrationForm({ onClose }) {
  const [formData, setFormData] = useState({
    // Basic details
    dealer_name: "",
    email: "",
    phone: "",
    password: "",
    confirm_password: "",
    skip_evaluation: false,
    // Business details
    company_type: "individual",
    mailing_address: "",
    // Bank details
    bank_name: "",
    bank_account_name: "",
    branch: "",
    ifsc_code: "",
    account_number: "",
    reenter_account_number: "",
  });

  // Partners state for partnership/LLP
  const [partners, setPartners] = useState([{ name: "" }]);

  const [focusedField, setFocusedField] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  const [accountNumberError, setAccountNumberError] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Partner field change handler
  const handlePartnerChange = (idx, value) => {
    setPartners((prev) => {
      const updated = [...prev];
      updated[idx].name = value;
      return updated;
    });
  };

  const addPartner = () => {
    setPartners((prev) => [...prev, { name: "" }]);
  };

  const removePartner = (idx) => {
    setPartners((prev) => prev.filter((_, i) => i !== idx));
  };

  // Reset partners if business type changes
  React.useEffect(() => {
    if (
      formData.company_type === "partnership" ||
      formData.company_type === "limited_liability_partnership"
    ) {
      if (partners.length === 0) setPartners([{ name: "" }]);
    } else {
      setPartners([{ name: "" }]);
    }
  }, [formData.company_type]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    let valid = true;
    if (formData.account_number !== formData.reenter_account_number) {
      setAccountNumberError("Account numbers do not match.");
      valid = false;
    } else {
      setAccountNumberError("");
    }
    if (formData.password !== formData.confirm_password) {
      setPasswordError("Passwords do not match.");
      valid = false;
    } else {
      setPasswordError("");
    }
    if (!valid) return;
    const submitData = {
      ...formData,
      partners:
        formData.company_type === "partnership" ||
        formData.company_type === "limited_liability_partnership"
          ? partners
          : undefined,
    };
    try {
      const response = await apiService.post("/manuallyRegisterDealer", {
        details: submitData,
      });
      console.log("API response:", response);
      if (response.error) {
        alert("Error: " + response.message);
        return;
      }
      console.log("Form submitted:", submitData);
      alert("Successfully registered dealer");
    } catch (error) {
      console.error("Error registering dealer:", error);
      alert("Error registering dealer. Please try again.");
    }
  };

  const currentBusinessFields = businessTypeFields[formData.company_type] || [];

  return (
    <div className={commonStyles.modalBackdrop}>
      <div className={commonStyles.modalLarge}>
        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          style={{
            position: "absolute",
            top: "18px",
            right: "24px",
            background: "transparent",
            border: "none",
            fontSize: "1.7rem",
            color: "#4a5568",
            cursor: "pointer",
            zIndex: 2,
          }}
          aria-label="Close"
        >
          &times;
        </button>
        <h1 style={styles.formTitle}>Dealer Registration</h1>

        <form onSubmit={handleSubmit}>
          {/* Basic Details Section */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Basic Details</h2>
            {/* ...existing code... */}
            <div style={styles.formGroup}>
              <label style={styles.label}>
                Dealer Name<span style={styles.required}>*</span>
              </label>
              <input
                type="text"
                name="dealer_name"
                value={formData.dealer_name}
                onChange={handleChange}
                onFocus={() => setFocusedField("dealer_name")}
                onBlur={() => setFocusedField(null)}
                style={{
                  ...styles.input,
                  ...(focusedField === "dealer_name" ? styles.inputFocus : {}),
                }}
                required
              />
            </div>

            <div style={styles.row}>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Email<span style={styles.required}>*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  onFocus={() => setFocusedField("email")}
                  onBlur={() => setFocusedField(null)}
                  style={{
                    ...styles.input,
                    ...(focusedField === "email" ? styles.inputFocus : {}),
                  }}
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Phone Number<span style={styles.required}>*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  onFocus={() => setFocusedField("phone")}
                  onBlur={() => setFocusedField(null)}
                  style={{
                    ...styles.input,
                    ...(focusedField === "phone" ? styles.inputFocus : {}),
                  }}
                  required
                />
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>
                Password<span style={styles.required}>*</span>
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                onFocus={() => setFocusedField("password")}
                onBlur={() => setFocusedField(null)}
                style={{
                  ...styles.input,
                  ...(focusedField === "password" ? styles.inputFocus : {}),
                }}
                required
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>
                Confirm Password<span style={styles.required}>*</span>
              </label>
              <input
                type="password"
                name="confirm_password"
                value={formData.confirm_password}
                onChange={handleChange}
                onFocus={() => setFocusedField("confirm_password")}
                onBlur={() => setFocusedField(null)}
                style={{
                  ...styles.input,
                  ...(focusedField === "confirm_password"
                    ? styles.inputFocus
                    : {}),
                }}
                required
              />
              {passwordError && (
                <div style={{ color: "#e53e3e", marginTop: "0.5rem" }}>
                  {passwordError}
                </div>
              )}
            </div>

            <div style={styles.formGroup}>
              <label style={{ ...styles.label, display: "flex", alignItems: "center", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  name="skip_evaluation"
                  checked={formData.skip_evaluation}
                  onChange={(e) => setFormData((prev) => ({
                    ...prev,
                    skip_evaluation: e.target.checked,
                  }))}
                  style={{
                    marginRight: "0.5rem",
                    width: "18px",
                    height: "18px",
                    cursor: "pointer",
                  }}
                />
                Skip Evaluation
              </label>
            </div>
          </div>

          {/* Business Details Section */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Business Details</h2>

            <div style={styles.formGroup}>
              <label style={styles.label}>
                Business Type<span style={styles.required}>*</span>
              </label>
              <select
                name="company_type"
                value={formData.company_type}
                onChange={handleChange}
                onFocus={() => setFocusedField("company_type")}
                onBlur={() => setFocusedField(null)}
                style={{
                  ...styles.select,
                  ...(focusedField === "company_type" ? styles.inputFocus : {}),
                }}
                required
              >
                {businessTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {currentBusinessFields.map((field) => (
              <div key={field.name} style={styles.formGroup}>
                <label style={styles.label}>
                  {field.label}
                  {field.required && <span style={styles.required}>*</span>}
                </label>
                <input
                  type={field.type}
                  name={field.name}
                  value={formData[field.name] || ""}
                  onChange={handleChange}
                  onFocus={() => setFocusedField(field.name)}
                  onBlur={() => setFocusedField(null)}
                  style={{
                    ...styles.input,
                    ...(focusedField === field.name ? styles.inputFocus : {}),
                  }}
                  required={field.required}
                />
              </div>
            ))}

            {/* Partners Section for Partnership/LLP */}
            {(formData.company_type === "partnership" ||
              formData.company_type === "limited_liability_partnership") && (
              <div style={{ marginTop: "2rem" }}>
                <h3 style={{ ...styles.sectionTitle, fontSize: "1.1rem" }}>
                  Partners
                </h3>
                {partners.map((partner, idx) => (
                  <div
                    key={idx}
                    style={{
                      ...styles.formGroup,
                      display: "flex",
                      gap: "1rem",
                      alignItems: "center",
                    }}
                  >
                    <input
                      type="text"
                      placeholder="Partner Name"
                      value={partner.name}
                      onChange={(e) => handlePartnerChange(idx, e.target.value)}
                      style={{
                        ...styles.input,
                        maxWidth: "220px",
                      }}
                      required
                    />
                    {partners.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removePartner(idx)}
                        style={{
                          background: "#e53e3e",
                          color: "white",
                          border: "none",
                          borderRadius: "4px",
                          padding: "0.5rem 0.75rem",
                          cursor: "pointer",
                        }}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addPartner}
                  style={{
                    background: "#3182ce",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    padding: "0.5rem 1rem",
                    marginTop: "0.5rem",
                    cursor: "pointer",
                  }}
                >
                  Add Partner
                </button>
              </div>
            )}

            <div style={styles.formGroup}>
              <label style={styles.label}>
                Mailing Address<span style={styles.required}>*</span>
              </label>
              <textarea
                name="mailing_address"
                value={formData.mailing_address}
                onChange={handleChange}
                onFocus={() => setFocusedField("mailing_address")}
                onBlur={() => setFocusedField(null)}
                style={{
                  ...styles.input,
                  minHeight: "80px",
                  resize: "vertical",
                  fontFamily: "inherit",
                }}
                required
              />
            </div>
          </div>

          {/* Bank Account Details Section */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Bank Account Details</h2>
            {/* ...existing code... */}
            <div style={styles.row}>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Bank Account Name<span style={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  name="bank_account_name"
                  value={formData.bank_account_name}
                  onChange={handleChange}
                  onFocus={() => setFocusedField("bank_account_name")}
                  onBlur={() => setFocusedField(null)}
                  style={{
                    ...styles.input,
                    ...(focusedField === "bank_account_name"
                      ? styles.inputFocus
                      : {}),
                  }}
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Bank Name<span style={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  name="bank_name"
                  value={formData.bank_name}
                  onChange={handleChange}
                  onFocus={() => setFocusedField("bank_name")}
                  onBlur={() => setFocusedField(null)}
                  style={{
                    ...styles.input,
                    ...(focusedField === "bank_name" ? styles.inputFocus : {}),
                  }}
                  required
                />
              </div>
            </div>

            <div style={styles.row}>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Branch<span style={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  name="branch"
                  value={formData.branch}
                  onChange={handleChange}
                  onFocus={() => setFocusedField("branch")}
                  onBlur={() => setFocusedField(null)}
                  style={{
                    ...styles.input,
                    ...(focusedField === "branch" ? styles.inputFocus : {}),
                  }}
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  IFSC Code<span style={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  name="ifsc_code"
                  value={formData.ifsc_code}
                  onChange={handleChange}
                  onFocus={() => setFocusedField("ifsc_code")}
                  onBlur={() => setFocusedField(null)}
                  style={{
                    ...styles.input,
                    ...(focusedField === "ifsc_code" ? styles.inputFocus : {}),
                  }}
                  required
                />
              </div>
            </div>

            <div style={styles.row}>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Account Number<span style={styles.required}>*</span>
                </label>
                <input
                  type="password"
                  name="account_number"
                  value={formData.account_number}
                  onChange={handleChange}
                  onFocus={() => setFocusedField("account_number")}
                  onBlur={() => setFocusedField(null)}
                  style={{
                    ...styles.input,
                    ...(focusedField === "account_number"
                      ? styles.inputFocus
                      : {}),
                  }}
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Re-enter Account Number<span style={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  name="reenter_account_number"
                  value={formData.reenter_account_number}
                  onChange={handleChange}
                  onFocus={() => setFocusedField("reenter_account_number")}
                  onBlur={() => setFocusedField(null)}
                  style={{
                    ...styles.input,
                    ...(focusedField === "reenter_account_number"
                      ? styles.inputFocus
                      : {}),
                  }}
                  required
                />
                {accountNumberError && (
                  <div style={{ color: "#e53e3e", marginTop: "0.5rem" }}>
                    {accountNumberError}
                  </div>
                )}
              </div>
            </div>
          </div>

          <button
            type="submit"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            style={{
              ...styles.submitButton,
              ...(isHovered ? styles.submitButtonHover : {}),
            }}
          >
            Register Dealer
          </button>
        </form>
      </div>
    </div>
  );
}
